/**
 * @module app.cm
 */

import { autoinject, Disposable, computedFrom, singleton, PLATFORM } from 'aurelia-framework';
import { Router, RouterConfiguration, RouteConfig, NavigationInstruction, NavModel } from 'aurelia-router';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { EventAggregator } from 'aurelia-event-aggregator';
import { IAppRouteConfig } from 'app/shared/config/router-types';
import { IQueueNavigationBinding, IQueueParameters } from 'app/core/models/essette-interfaces';
import { ICasePageParameters } from 'app/cm/pages/case/case-page';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { guid } from 'app/shared/lib/guid';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IHouseholdCasePageParameters extends Partial<IQueueParameters> {
    householdCaseId: string;
    queueTypeCode?: 'CM.Routing';
}

interface MemberWithMembership extends CD.Member {
    membership: CD.HouseholdMembership;
}

export interface IMemberWithCase {
    member: MemberWithMembership;
    case: CM.Case | undefined;
}

@autoinject()
export class HouseholdCasePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected householdCase: CM.HouseholdCase = {};
    protected caseSettings: IApiSettings = {};
    protected memberCases: IMemberWithCase[] = [];
    protected currentRoute: string = '';
    protected queueBinding: IQueueNavigationBinding | null = null;

    public hasCreateAdditional: boolean = false;

    @computedFrom('householdCase', 'householdCase.id')
    private get householdCaseId(): string | null {
        return this.householdCase && this.householdCase.id || null;
    }

    protected householdCaseNavTabIds: string[] = [];

    ////****************** Element References ***************************************////


    ////****************** Event Subscriptions **************************************////
    private navigationCompletedEvent: Disposable | null = null;


    ////****************** Lifecycle events (in order of execution) *****************////
    //TODO: Get rid of event aggregator if possible
    constructor(
        private readonly caseService: CaseService,
        @singleton(true) private readonly context: HouseholdCasePageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(params: IHouseholdCasePageParameters, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(params.householdCaseId && params.householdCaseId !== this.householdCase.id) {

            const [loadedHouseholdCase, settings] = await Promise.all([
                this.loadHouseholdCase(params.householdCaseId),
                this.loadSettings()
            ]);

            this.householdCase = this.context.householdCase = loadedHouseholdCase;
            this.context.settings = this.caseSettings = settings;

            this.checkPermissions();

            this.memberCases = this.formatMemberCases();

            if(routeConfig.navModel) {
                routeConfig.navModel.setTitle(`Household Case #${this.householdCase.number}`);
            }
        }
    }

    protected attached(): void {
        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.navigationComplete());
        this.navigationComplete();
    }

    protected detached(): void {
        if(this.navigationCompletedEvent) { this.navigationCompletedEvent.dispose(); this.navigationCompletedEvent = null; }
    }


    ////****************** Events ***************************************************////

    protected navigationComplete(): void {
        const routeConfig = this.router.currentInstruction.config;
        const route = Array.isArray(routeConfig.route) ? routeConfig.route[0] : routeConfig.route;
        const tab = routeConfig.settings && (routeConfig.settings.tab as string) || '';
        if(routeConfig && (routeConfig.nav || tab)) {
            this.currentRoute = `/${tab || route}`;
        }
    }


    ////****************** Functions ************************************************////

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCreateAdditional = this.accountContext.hasPermission('CM.Case.Duty.Additional');
    }

    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        await this.accountContext.load();
        const routes: IAppRouteConfig[] = [
            { route: '', redirect: 'careplan' },
            // Tab Navigation
            {
                route: 'assessments', name: 'cm.household-case.assessments', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-assessments-page'), title: 'cm.household-case.assessments', nav: true,
                settings: { icon: 'ui:icons.assessment', isVisible: this.accountContext.hasRouteAccess('cm.household-case.assessments'), topic: 'cm.household-case.assessments' }
            },
            {
                route: 'careteam', name: 'cm.household-case.careteam', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-care-team-page'), title: 'cm.household-case.careteam', nav: true,
                settings: { icon: 'ui:icons.users', isVisible: this.accountContext.hasRouteAccess('cm.household-case.careteam'), topic: 'cm.household-case.careteam' }
            },
            {
                route: 'careplan', name: 'cm.household-case.careplan', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-care-plan-page'), title: 'cm.household-case.careplan', nav: true,
                settings: { icon: 'ui:icons.carePlan', isVisible: this.accountContext.hasRouteAccess('cm.household-case.careplan'), topic: 'cm.household-case.careplan' }
            },
            {
                route: 'tasks', name: 'cm.household-case.tasks', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-tasks-page'), title: 'cm.household-case.tasks', nav: true,
                settings: { icon: 'ui:icons.tasks', isVisible: this.accountContext.hasRouteAccess('cm.household-case.tasks'), topic: 'cm.household-case.tasks' }
            },
            {
                route: 'notes', name: 'cm.household-case.notes', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-notes-page'), title: 'cm.household-case.notes', nav: true,
                settings: { icon: 'ui:icons.note', isVisible: this.accountContext.hasRouteAccess('cm.household-case.notes'), topic: 'cm.household-case.notes' }
            },
            {
                route: 'correspondence', name: 'cm.household-case.correspondence', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-correspondence-page'), title: 'cm.household-case.correspondence', nav: true,
                settings: { icon: 'ui:icons.correspondence.general', isVisible: this.accountContext.hasRouteAccess('cm.household-case.correspondence'), topic: 'cm.household-case.correspondence' }
            },
            {
                route: 'documents', name: 'cm.household-case.documents', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-documents-page'), title: 'cm.household-case.documents', nav: true,
                settings: { icon: 'ui:icons.document', isVisible: this.accountContext.hasRouteAccess('cm.household-case.documents'), topic: 'cm.household-case.documents' }
            },
            {
                route: 'diagnoses', name: 'cm.household-case.diagnoses', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-diagnoses-page'), title: 'cm.household-case.diagnoses', nav: true,
                settings: { icon: 'ui:icons.diagnoses', isVisible: this.accountContext.hasRouteAccess('cm.household-case.diagnoses'), topic: 'cm.household-case.diagnoses' }
            },
            {
                route: 'status', name: 'cm.household-case.status', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-status-page'), title: 'cm.household-case.status', nav: true,
                settings: { icon: 'ui:icons.history', isVisible: this.accountContext.hasRouteAccess('cm.household-case.status'), topic: 'cm.household-case.status' }
            },
            {
                route: 'routing', name: 'cm.household-case.routing', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-routing-page'), title: 'cm.household-case.routing', nav: true,
                settings: { icon: 'ui:icons.send', isVisible: this.accountContext.hasRouteAccess('cm.household-case.routing'), topic: 'cm.household-case.routing' }
            },
            {
                route: 'services', name: 'cm.household-case.services', moduleId: PLATFORM.moduleName('app/cm/pages/household/household-case-services-page'), title: 'cm.household-case.services', nav: true,
                settings: { icon: 'ui:icons.service', isVisible: this.accountContext.hasRouteAccess('cm.household-case.services'), topic: 'cm.household-case.services' }
            }
        ];

        config.map(routes);

        config.mapUnknownRoutes('app/shared/components/not-found');

        this.router = router;
    }

    protected householdCaseNavigationTabs(): Array<NavModel> {
        const tabs = this.router.navigation.filter(tab => tab.config.settings.isVisible);
        this.householdCaseNavTabIds.splice(0);
        tabs.forEach(() => { this.householdCaseNavTabIds.push(guid()); });
        return tabs;
    }

    protected generateTabLink(caseId: string | null, memberId: string): string {
        if(!caseId) {
            return this.hasCreateAdditional ? `#/cm/manage-cases/create/${memberId}/\?origin=CM.HouseholdCase&householdCaseId=${this.householdCase.id}` : '#void';
        }
        return this.router.parent.generate('cm.case', { caseId: caseId });
    }

    protected getQueueNavigationView(): string | undefined {
        if(!this.queueBinding) { return; }
        const views = this.env.getDynamicViewsByKey('Core.QueueType', this.queueBinding.queueTypeCode);
        return views[0]?.url;
    }

    private formatMemberCases(): IMemberWithCase[] {
        if(!this.householdCase?.household?.members) return [];

        const membersWithCases: IMemberWithCase[] = [];

        for(const m of this.householdCase?.household?.members) {
            const mwc: IMemberWithCase = {
                member: m as MemberWithMembership,
                case: this.householdCase.cases?.find(mc => mc.member?.id === m.id)
            };

            // goal is to have add cases shown on left side and existing cases shown on right side
            // only show tab if member's membership is active
            if(mwc.member.membership.isActive) {
                // if case is present, add the element to the end of the array, making the button appear on right side
                if(mwc.case) {
                    membersWithCases.push(mwc);
                }
                // if there is no case, add the element to the start of the array, making the button appear on left side
                else {
                    membersWithCases.unshift(mwc);
                }
            }
        }

        return membersWithCases;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadSettings(): Promise<IApiSettings> {
        try {
            this.state.beginLoad();

            const response = await this.caseService.loadCaseSettings();
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadHouseholdCase(householdCaseId: string): Promise<CM.HouseholdCase> {
        const includes: string[] = [
            'household',
            'household.members',
            'household.members.membership',
            'cases',
            'assignedTo',
            'program',
            'acuity',
            'intensity',
            'status'
        ];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadHouseholdCase(householdCaseId, includes);
            return response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
